import { ConfirmationModalComponent, Input } from 'commons/components';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { PocRadio } from 'commons/components/components-wrapper/poc-radio.component';
import { PocRadioGroup } from 'commons/components/components-wrapper/poc-radio-group.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import React, { useState } from 'react';
import { PopupProps, usePopupManager } from 'react-popup-manager';

import type { PocInputValidity } from 'commons/types';
import { CUSTOM_FIELD_TYPE_OPTIONS } from 'core/service/custom-fields/custom-field.mapper';
import { CustomField, NAME_MAX_LENGTH } from 'core/service/custom-fields/custom-field.type';
import FeatureFlagGuard from 'core/auth/feature-flag-guard.component';
import { UNHANDLED_EXCEPTION } from 'core/constants/constants';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { useTranslation } from 'core/i18n/i18n';
import { executeRequest, GraphqlError } from 'core/utils/backend-service';
import { showSuccessNotification } from 'core/utils/notification-service';
import { getNameValidationMessages } from './custom-field-modal.validation';
import { CustomFieldModalStyled } from './custom-field.style';
import { DEVICE_MANAGEMENT_FEATURE_FLAG } from 'core/service/feature-flag/feature-flag.constants';

// To extract to file when final version
export type CustomFieldValidation = {
    name?: PocInputValidity;
};

export const defaultCustomFieldValidation = {
    name: undefined,
};

export interface CustomFieldModalComponentProps extends PopupProps {
    customField: CustomField;
    afterActions: VoidFunction;
    createCustomField: (customField: CustomField) => Promise<void>;
    updateCustomField: (customField: CustomField) => Promise<void>;
    deleteCustomField: (customFieldId: string) => Promise<void>;
}

export const CustomFieldModalComponent: React.FC<CustomFieldModalComponentProps> = (props) => {
    const { customField, afterActions, onClose, createCustomField, updateCustomField, deleteCustomField } = props;
    const [customFieldData, setCustomFieldData] = useState<CustomField>(customField);
    const [customFieldValidation, setCustomFieldValidation] = useState<CustomFieldValidation>(
        structuredClone(defaultCustomFieldValidation)
    );
    const [isVisibleModal, setIsVisibleModal] = useState<boolean>(true);
    const customValidationCodeRef = React.useRef(undefined);

    const popupManager = usePopupManager();
    const { t } = useTranslation();

    const { mutateAsync: saveCustomField, isPending: isSaving } = usePocMutation({
        mutationFn: (data: CustomField) =>
            executeRequest(() => (data.id ? updateCustomField : createCustomField)(data)),
    });

    const { mutateAsync: removeCustomField } = usePocMutation({
        mutationFn: (id: string) => executeRequest(() => deleteCustomField(id)),
    });

    const handleOnChange = (field: string, value: string | null) => {
        setCustomFieldData({ ...customFieldData, [field as keyof CustomField]: value?.trimStart() });
    };

    const handleCustomFieldValidation = (field: string, value: PocInputValidity) => {
        setCustomFieldValidation({ ...customFieldValidation, [field]: value });
    };

    const handleOnClose = () => {
        setIsVisibleModal(false);
        onClose?.();
    };

    const handleOnSave = () => {
        customValidationCodeRef.current = undefined;

        const handlePositiveNotification = () => {
            showSuccessNotification(
                t(
                    `settings.system.custom-fields.fields.modal.${
                        customFieldData.id ? 'edit' : 'add'
                    }.notification.positive`,
                    { name: customFieldData.name }
                )
            );
            handleOnClose();
            afterActions();
        };

        const handleCustomFieldError = (errorString: Error) => {
            const error = JSON.parse(errorString.message);
            if (error.errors?.length > 0 && error.errors[0].extensions.code !== UNHANDLED_EXCEPTION) {
                const errorFields = (error.errors as GraphqlError[]).reduce<
                    Record<string, { state: string; type: string[] }>
                >((acc, e) => {
                    const field = e.extensions?.field?.split('.').at(-1);
                    return field ? { ...acc, [field]: { state: 'error', type: ['custom'] } } : acc;
                }, {});
                if (Object.keys(errorFields).length > 0 && customFieldValidation) {
                    customValidationCodeRef.current = error.errors[0]?.extensions?.type;
                    setCustomFieldValidation({ ...customFieldValidation, ...errorFields });
                }
            } else {
                handleOnClose();
                throw error;
            }
        };

        saveCustomField(customFieldData).then(handlePositiveNotification).catch(handleCustomFieldError);
    };
    const handleDeleteSuccess = () => {
        showSuccessNotification(
            t('settings.system.custom-fields.fields.modal.delete.notification.positive', {
                name: customFieldData.name,
            })
        );
    };

    const handleDeleteFinally = () => {
        popupManager.closeAll();
        afterActions();
    };

    const handleOnRemove = () => {
        setIsVisibleModal(false);

        const onCloseModal = () => setIsVisibleModal(true);

        const onActionClick = () =>
            removeCustomField(customFieldData.id!).then(handleDeleteSuccess).finally(handleDeleteFinally);

        popupManager.open(ConfirmationModalComponent, {
            title: t('settings.system.custom-fields.fields.modal.delete.title', { name: customFieldData.name }),
            children: <>{t('settings.system.custom-fields.fields.modal.delete.description')}</>,
            buttonText: t('settings.system.custom-fields.fields.modal.delete.button'),
            executeOnCloseOnAction: false,
            onClose: onCloseModal,
            actionOnClick: onActionClick,
        });
    };

    const customValidationCode = customValidationCodeRef.current;

    return (
        <CustomFieldModalStyled
            title={t(`settings.system.custom-fields.fields.modal.${customFieldData.id ? 'edit' : 'create'}.title`)}
            footerOptions={
                <PocButton
                    variant="primary"
                    type="submit"
                    size="medium"
                    onClick={handleOnSave}
                    disabled={!customFieldData.name || isSaving}
                >
                    {t(`settings.system.custom-fields.fields.modal.${customFieldData.id ? 'edit' : 'create'}.button`)}
                </PocButton>
            }
            onClose={handleOnClose}
            isOpen={isVisibleModal}
            isForm
            loading={isSaving}
        >
            <Input
                name={'name'}
                label={t('settings.system.custom-fields.fields.modal.create.name.label')}
                placeholder={t('settings.system.custom-fields.fields.modal.create.name.placeholder')}
                spaceForError
                validity={customFieldValidation.name}
                customValidationCode={customValidationCode}
                value={customFieldData.name}
                maxlength={NAME_MAX_LENGTH}
                required
                validationMessages={getNameValidationMessages(t)}
                onValidity={handleCustomFieldValidation}
                onChange={handleOnChange}
            />

            {!customFieldData.id && (
                <FeatureFlagGuard featureFlag={DEVICE_MANAGEMENT_FEATURE_FLAG}>
                    <PocTypography variant="subtitle1" element="span">
                        {t('settings.system.custom-fields.fields.modal.module.label')}
                    </PocTypography>

                    <PocRadioGroup
                        name={'custom-field-type'}
                        direction={'horizontal'}
                        value={customFieldData.type ?? CUSTOM_FIELD_TYPE_OPTIONS.OPERATORS}
                        onValueChange={(ev) => {
                            ev.stopPropagation();
                            handleOnChange('type', ev.detail);
                        }}
                    >
                        <PocRadio value={CUSTOM_FIELD_TYPE_OPTIONS.OPERATORS}>
                            {t('settings.system.custom-fields.fields.modal.module.label.operators')}
                        </PocRadio>
                        <PocRadio value={CUSTOM_FIELD_TYPE_OPTIONS.DEVICES}>
                            {t('settings.system.custom-fields.fields.modal.module.label.devices')}
                        </PocRadio>
                    </PocRadioGroup>
                </FeatureFlagGuard>
            )}

            {customFieldData.id ? (
                <PocButton variant="tertiary" status="alarm" size="medium" onClick={handleOnRemove}>
                    {t('settings.system.custom-fields.fields.modal.delete.action')}
                </PocButton>
            ) : undefined}
        </CustomFieldModalStyled>
    );
};
